import { SvgIcon, TextInput } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { useDebounce } from '@/hooks/use-debounce';
import { colors } from '@/theme/colors';
import React, { memo, startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, TextInput as RNTextInput, TouchableOpacity, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';

import { usePlayerListTemplate } from './context';
import { markSlot } from './slot-marker';

export type PlayerListTemplateV2SearchBarProps = {
  placeholder?: string;
  /** Override `listLogic.searchText` from context. */
  value?: string;
  /** Override context wiring. Receives the new (debounced) text. */
  onChangeText?: (text: string) => void;
  /** Override context wiring. Called immediately on clear. */
  onClear?: () => void;
  /** Debounce for text change. Default: 1500ms. */
  debounceMs?: number;
};

function SearchBarComponent({
  placeholder,
  value,
  onChangeText,
  onClear,
  debounceMs = 1500,
}: PlayerListTemplateV2SearchBarProps) {
  const styles = useStyles();
  const { scale } = useScaling();
  const { listLogic, searchPlaceholder } = usePlayerListTemplate();

  const resolvedValue = value ?? listLogic.searchText;
  const resolvedPlaceholder = placeholder ?? searchPlaceholder;

  const [innerValue, setInnerValue] = useState(resolvedValue);
  const inputRef = useRef<RNTextInput>(null);

  // `KeyboardController.dismiss()` alone doesn't dismiss the IME on Samsung DeX / freeform Android
  // windows because the inset controller can't always reach the IME bound to the freeform window.
  // Blurring the focused TextInput is the most universal way: removing focus releases the IME
  // target, after which the soft keyboard hides naturally on every platform/mode. Keep
  // `Keyboard.dismiss()` + `KeyboardController.dismiss()` as fallbacks for legacy + Reanimated paths.
  const dismissKeyboard = useCallback(() => {
    inputRef.current?.blur();
    Keyboard.dismiss();
    KeyboardController.dismiss();
  }, []);

  useEffect(() => {
    setInnerValue(resolvedValue);
  }, [resolvedValue]);

  const handleChange = useCallback(
    (text: string) => {
      if (onChangeText) {
        onChangeText(text);
        return;
      }
      startTransition(() => {
        listLogic.onChangePage?.(1);
        listLogic.onChangeSearchText?.(text);
      });
    },
    [listLogic, onChangeText],
  );

  const handleClearText = useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }
    startTransition(() => {
      listLogic.onChangePage?.(1);
      listLogic.onChangeSearchText?.('');
    });
  }, [listLogic, onClear]);

  const debouncedOnChangeText = useDebounce((newValue: string) => {
    handleChange(newValue);
    dismissKeyboard();
  }, debounceMs);

  const debouncedOnClear = useDebounce(
    () => {
      handleClearText();
      dismissKeyboard();
    },
    1000,
    { leading: true },
  );

  const handleChangeText = (text: string) => {
    setInnerValue(text);
    debouncedOnChangeText(text);
  };

  const handleClear = () => {
    debouncedOnChangeText.cancel();
    setInnerValue('');
    debouncedOnClear();
  };

  return (
    <TextInput value={innerValue} onChangeText={handleChangeText} status="default">
      <TextInput.Content style={styles.content}>
        <TextInput.Icon position="left" name="IcSearch" size={scale(20)} color={colors['Blue 2']} />
        <TextInput.Field
          ref={inputRef}
          placeholder={resolvedPlaceholder}
          keyboardType="default"
          maxLength={100}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {innerValue && innerValue.length > 0 ? (
          <TouchableOpacity onPress={handleClear} activeOpacity={0.8} style={styles.clearButton}>
            <SvgIcon name="IcClose" size={scale(12)} color={colors['5']} />
          </TouchableOpacity>
        ) : (
          <View style={styles.clearButtonPlaceholder} />
        )}
      </TextInput.Content>
    </TextInput>
  );
}

const MemoSearchBar = memo(SearchBarComponent);
MemoSearchBar.displayName = 'PlayerListTemplateV2.SearchBar';

export const SearchBar = markSlot(MemoSearchBar, 'search');

const useStyles = createStyles(({ scale }) => ({
  content: {
    backgroundColor: colors['0'],
    borderColor: 'transparent',
    borderWidth: 0,
    height: scale(42),
  },
  clearButton: {
    width: scale(24),
    height: scale(24),
    justifyContent: 'center',
    alignItems: 'center',
  },
  clearButtonPlaceholder: {
    width: scale(24),
    height: scale(24),
  },
}));
